import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, ArrowLeft, Save, Sparkles, Users, Plus, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { celebrationService } from '@/lib/api/celebration.service';
import {
  createCelebrationWithWorkflow,
  type CelebrationCreateCelebrantSeed,
} from '@/lib/celebrations/createCelebrationWorkflow';
import { clearCrossModeDraft, writeCrossModeDraft } from '@/lib/celebrations/crossModeDraft';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { CelebrationCelebrants } from '@/components/CelebrationCelebrants';
import { TeamMembersList } from '@/components/TeamMembers/TeamMembersList';
import { AddTeamMemberForm } from '@/components/TeamMembers/AddTeamMemberForm';
import { EditTeamMemberModal } from '@/components/TeamMembers/EditTeamMemberModal';
import { LocationAutocomplete } from '@/components/LocationAutocomplete';
import { teamMemberService } from '@/lib/api/teamMember.service';
import { buildFormOptions, getSubtypesForType, getEmojiForType, getLabelForType, getSubtypeLabel } from '@/config/celebrationRegistry';
import {
  getMissingCelebrationFieldLabels,
  getMissingCelebrationFields,
  type CelebrationMissingFieldKey,
} from '@/utils/celebrationMissingFields';
import type { CreateCelebrationDto } from '@/types/celebration.types';
import type { TeamMemberWithUser } from '@/types/teamMember.types';
import type { CelebrationCelebrant } from '@/types/user-contact.types';
import {
  CelebrationGuestsCard,
  type GuestInviteMode,
  type LinkAccessType,
  type EmailDispatchMode,
} from '@/components/celebrations/CelebrationGuestsCard';
import { CelebrationQuotasSection } from '@/components/celebrations/CelebrationQuotasSection';

export interface WizardPresets {
  celebrationType: string;
  celebrationSubtype: string | null;
  templateKey: string | null;
  initialData?: Partial<CreateCelebrationDto>;
  initialCelebrantNames?: string[];
  chatSessionId?: string;
  confirmedTags?: Record<string, string[]>;
}

interface CelebrationFormProps {
  mode?: 'create' | 'edit';
  /** When provided, type/subtype are pre-set and read-only */
  wizardPresets?: WizardPresets;
  /** Hide back button + header (wizard provides its own) */
  embedded?: boolean;
  /** External back handler (wizard step navigation) */
  onBack?: () => void;
}

const SECTION_IDS = {
  basicDetails: 'celebration-basic-details',
  celebrants: 'celebration-celebrants',
  teamMembers: 'celebration-team-members',
  quotas: 'guest-quotas',
} as const;

const celebrationFormOptions = buildFormOptions();

type ApiErrorLike = {
  response?: {
    data?: {
      message?: string;
      error?: string;
    };
  };
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error !== 'object' || error === null) {
    return fallback;
  }

  const apiError = error as ApiErrorLike;
  return apiError.response?.data?.message ?? apiError.response?.data?.error ?? fallback;
};

const getCelebrationTheme = (type: string) => {
  const themes: Record<string, { gradient: string; border: string; ring: string; badgeBg: string }> = {
    wedding: { gradient: 'from-rose-100 via-pink-50 to-white', border: 'border-rose-100', ring: 'ring-rose-200', badgeBg: 'bg-rose-50' },
    birthday: { gradient: 'from-amber-100 via-orange-50 to-white', border: 'border-amber-100', ring: 'ring-amber-200', badgeBg: 'bg-amber-50' },
    graduation: { gradient: 'from-violet-100 via-purple-50 to-white', border: 'border-violet-100', ring: 'ring-violet-200', badgeBg: 'bg-violet-50' },
    anniversary: { gradient: 'from-emerald-100 via-teal-50 to-white', border: 'border-emerald-100', ring: 'ring-emerald-200', badgeBg: 'bg-emerald-50' },
    baby_shower: { gradient: 'from-sky-100 via-blue-50 to-white', border: 'border-sky-100', ring: 'ring-sky-200', badgeBg: 'bg-sky-50' },
    default: { gradient: 'from-blue-100 via-indigo-50 to-white', border: 'border-blue-100', ring: 'ring-blue-200', badgeBg: 'bg-blue-50' },
  };

  return themes[type] ?? themes.default;
};

export const CelebrationForm: React.FC<CelebrationFormProps> = ({ mode = 'create', wizardPresets, embedded, onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation(['celebrations', 'common']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(mode === 'edit');

  // Team management state (only for edit mode)
  const [showAddTeamForm, setShowAddTeamForm] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMemberWithUser | null>(null);
  const [teamRefreshTrigger, setTeamRefreshTrigger] = useState(0);
  const [teamError, setTeamError] = useState<string | null>(null);

  const initialCelebrants: CelebrationCelebrant[] = (wizardPresets?.initialCelebrantNames ?? []).map((name, index) => ({
    id: Date.now() + index,
    celebration_id: 0,
    user_id: -(index + 1),
    role: 'Celebrant',
    is_surprise: false,
    is_team_member: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    user_contact: {
      id: Date.now() + index,
      owner_user_id: 0,
      user_id: -(index + 1),
      full_name: name,
      email: '',
      phone: '',
      relationship: '',
      is_primary: false,
      status: 'active',
      dietary_preferences: '',
      special_requirements: '',
      metadata: { source: 'chat_prefill' },
      source: 'celebration_celebrant',
      created_at: new Date(),
      updated_at: new Date(),
    },
  }));
  const [localCelebrants, setLocalCelebrants] = useState<CelebrationCelebrant[]>(initialCelebrants);

  const initialStartDate = wizardPresets?.initialData?.start_date ?? '';
  const initialEndDate = wizardPresets?.initialData?.end_date ?? initialStartDate;

  const getUserProvidedCelebrationParagraph = (): string => {
    if (typeof formData.description !== 'string') return '';
    return formData.description.trim();
  };

  const [formData, setFormData] = useState<CreateCelebrationDto>({
    title: wizardPresets?.initialData?.title ?? '',
    description: wizardPresets?.initialData?.description ?? '',
    celebration_type: wizardPresets?.celebrationType ?? 'wedding',
    celebration_subtype: wizardPresets?.celebrationSubtype ?? undefined,
    start_date: initialStartDate,
    end_date: initialEndDate,
    budget: wizardPresets?.initialData?.budget,
    location_name: wizardPresets?.initialData?.location_name ?? '',
    latitude: wizardPresets?.initialData?.latitude,
    longitude: wizardPresets?.initialData?.longitude,
    radius_miles: wizardPresets?.initialData?.radius_miles ?? 10,
    template: wizardPresets?.templateKey ?? undefined,
  });

  // Guest & Invitation State
  const [guestMode, setGuestMode] = useState<GuestInviteMode>('link');
  const [linkAccess, setLinkAccess] = useState<LinkAccessType>('open');
  const [rsvpDeadline, setRsvpDeadline] = useState(formData.rsvp_deadline || '');
  const [allowPlusOnes, setAllowPlusOnes] = useState(false);
  const [plusOnesLimit, setPlusOnesLimit] = useState(1);
  const [guestEmails, setGuestEmails] = useState<string[]>([]);
  const [emailDispatchMode, setEmailDispatchMode] = useState<EmailDispatchMode>('immediate');
  const [guestCountEstimate, setGuestCountEstimate] = useState<number | undefined>(formData.guest_count_estimate);
  const [trackDietary, setTrackDietary] = useState(true);
  const [seatingLayoutEnabled, setSeatingLayoutEnabled] = useState(false);
  const [groupTagsEnabled, setGroupTagsEnabled] = useState(false);

  useEffect(() => {
    if (mode !== 'create') {
      return;
    }

    const celebrantNames = localCelebrants
      .map((celebrant) => celebrant.user_contact?.full_name || celebrant.name || '')
      .map((name) => name.trim())
      .filter(Boolean);

    const celebrantRoles = localCelebrants
      .map((celebrant) => celebrant.role || 'Celebrant')
      .map((role) => role.trim())
      .filter(Boolean);

    const celebrantEmails = localCelebrants
      .map((celebrant) => celebrant.user_contact?.email || '')
      .map((email) => email.trim())
      .filter(Boolean);

    writeCrossModeDraft({
      title: formData.title?.trim() || undefined,
      description: formData.description?.trim() || undefined,
      celebration_type: formData.celebration_type,
      celebration_subtype: formData.celebration_subtype,
      template: formData.template,
      start_date: formData.start_date || undefined,
      end_date: formData.end_date || undefined,
      budget: typeof formData.budget === 'number' ? formData.budget : undefined,
      location_name: formData.location_name?.trim() || undefined,
      latitude: typeof formData.latitude === 'number' ? formData.latitude : undefined,
      longitude: typeof formData.longitude === 'number' ? formData.longitude : undefined,
      radius_miles: typeof formData.radius_miles === 'number' ? formData.radius_miles : undefined,
      rsvp_deadline: formData.rsvp_deadline || undefined,
      celebrant_names: celebrantNames.length > 0 ? celebrantNames : undefined,
      celebrant_roles: celebrantRoles.length > 0 ? celebrantRoles : undefined,
      celebrant_emails: celebrantEmails.length > 0 ? celebrantEmails : undefined,
    });
  }, [mode, formData, localCelebrants]);

  useEffect(() => {
    if (mode === 'edit') {
      if (!id || isNaN(Number(id)) || Number(id) <= 0) {
        setError('Invalid celebration ID.');
        setTimeout(() => navigate('/celebrations'), 1500);
      }
    }
  }, [mode, id, navigate]);

  useEffect(() => {
    if (mode === 'edit' && id) {
      loadCelebrationData(parseInt(id));
    }
  }, [mode, id]);

  useEffect(() => {
    if (mode === 'edit' && !initialLoading && location.hash === `#${SECTION_IDS.quotas}`) {
      document.getElementById(SECTION_IDS.quotas)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [mode, initialLoading, location.hash]);

  const loadCelebrationData = async (celebrationId: number) => {
    try {
      setInitialLoading(true);
      const celebration = await celebrationService.getCelebration(celebrationId);
      
      const formatDateForInput = (isoDateString: string | null | undefined): string => {
        if (!isoDateString) return '';
        try {
          const date = new Date(isoDateString);
          // Format as YYYY-MM-DD for date input
          return date.toISOString().slice(0, 10);
        } catch {
          return '';
        }
      };

      const startDateInput = formatDateForInput(celebration.start_date);
      const endDateInputRaw = formatDateForInput(celebration.end_date);
      const endDateInput = endDateInputRaw || startDateInput;

      setFormData({
        title: celebration.title,
        description: celebration.description || '',
        celebration_type: celebration.celebration_type,
        celebration_subtype: celebration.celebration_subtype || undefined,
        start_date: startDateInput,
        end_date: endDateInput,
        budget: celebration.budget,
        location_name: celebration.location_name,
        latitude: celebration.latitude,
        longitude: celebration.longitude,
        radius_miles: celebration.radius_miles || 10,
        template: celebration.template,
      });
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to load celebration'));
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => {
      const nextValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
      const nextFormData = {
        ...prev,
        [name]: nextValue,
      };

      if (name === 'start_date') {
        const startDateValue = typeof nextValue === 'string' ? nextValue : '';
        const previousEndDate = typeof prev.end_date === 'string' ? prev.end_date : '';

        if (!previousEndDate || (startDateValue && previousEndDate < startDateValue)) {
          nextFormData.end_date = startDateValue;
        }
      }

      if (name === 'end_date') {
        const endDateValue = typeof nextValue === 'string' ? nextValue : '';
        const existingStartDate = typeof prev.start_date === 'string' ? prev.start_date : '';

        if (endDateValue && existingStartDate && endDateValue < existingStartDate) {
          nextFormData.end_date = existingStartDate;
        }
      }

      return nextFormData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const effectiveEndDate = formData.end_date || formData.start_date;

      if (formData.start_date && effectiveEndDate && effectiveEndDate < formData.start_date) {
        setError(t('celebrations:form.validation.endDateBeforeStart'));
        setLoading(false);
        return;
      }

      const formatDateForBackend = (dateString: string | undefined): string => {
        if (!dateString) return '';
        try {
          // For date-only inputs, create date at midnight UTC
          const date = new Date(dateString + 'T00:00:00.000Z');
          return date.toISOString();
        } catch {
          return '';
        }
      };

      const submissionData = {
        ...formData,
        start_date: formatDateForBackend(formData.start_date),
        end_date: formatDateForBackend(effectiveEndDate),
      };

      if (mode === 'edit' && id) {
        const updatedCelebration = await celebrationService.updateCelebration(parseInt(id), submissionData);

        // Stay on the edit page after save. Redirect only if backend returns a different id.
        const currentId = parseInt(id, 10);
        if (updatedCelebration.id !== currentId) {
          navigate(`/celebrations/${updatedCelebration.id}/edit`, { replace: true });
        }

        return;
      }

      const celebrantSeeds: CelebrationCreateCelebrantSeed[] = localCelebrants.map((celebrant, index) => ({
        name: celebrant.user_contact?.full_name || celebrant.name || `Celebrant ${index + 1}`,
        role: celebrant.role || 'Celebrant',
        email: celebrant.user_contact?.email,
        userId: celebrant.user_id,
        isSurprise: celebrant.is_surprise,
      }));

      const result = await createCelebrationWithWorkflow({
        celebrationData: submissionData,
        celebrants: celebrantSeeds,
        paragraphContent: getUserProvidedCelebrationParagraph(),
        confirmedTags: wizardPresets?.confirmedTags,
        chatSessionId: wizardPresets?.chatSessionId,
      });

      if (result.warnings.length > 0) {
        console.warn('[CelebrationForm] Creation completed with warnings:', result.warnings);
      }

      clearCrossModeDraft();

      navigate(`/celebrations/${result.celebrationId}/setup`);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, `Failed to ${mode} celebration`));
    } finally {
      setLoading(false);
    }
  };

  // Team management handlers
  const handleAddTeamSuccess = () => {
    setShowAddTeamForm(false);
    setTeamRefreshTrigger(prev => prev + 1);
  };

  const handleEditTeamSuccess = () => {
    setEditingMember(null);
    setTeamRefreshTrigger(prev => prev + 1);
  };

  const handleRemoveTeamMember = async (member: TeamMemberWithUser) => {
    if (!window.confirm(t('celebrations:form.teamSection.removeConfirm', { name: member.user.full_name }))) return;
    try {
      setTeamError(null);
      await teamMemberService.removeTeamMember(parseInt(id!), member.id);
      setTeamRefreshTrigger(prev => prev + 1);
    } catch (err: unknown) {
      setTeamError(getApiErrorMessage(err, 'Failed to remove team member'));
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Sparkles className="h-5 w-5 animate-pulse" />
          <span>{t('common:status.loading')}</span>
        </div>
      </div>
    );
  }

  const subtypeOptions = getSubtypesForType(formData.celebration_type);
  const missingFieldKeys = mode === 'edit' ? getMissingCelebrationFields(formData) : [];
  const missingFieldLabels = getMissingCelebrationFieldLabels(formData);
  const missingFieldKeySet = new Set<CelebrationMissingFieldKey>(missingFieldKeys);
  const theme = getCelebrationTheme(formData.celebration_type);
  const shortcutItems = [
    { id: SECTION_IDS.basicDetails, label: t('celebrations:form.sections.basicDetails') },
    { id: SECTION_IDS.celebrants, label: t('celebrations:form.sections.celebrants') },
    { id: SECTION_IDS.teamMembers, label: t('celebrations:form.sections.teamMembers') },
  ];

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (mode === 'edit' && id) {
      navigate(`/celebrations/${id}`);
      return;
    }

    navigate('/celebrations');
  };

  const isFieldMissing = (field: CelebrationMissingFieldKey): boolean => {
    return mode === 'edit' && missingFieldKeySet.has(field);
  };

  // When embedded in the wizard, render just the form and let the wizard provide shell/header.
  const formContent = (
    <div className={embedded ? '' : 'w-full'}>
      {/* Compact Header — only when NOT embedded in wizard */}
      {!embedded && (
        <div className={`mb-4 rounded-xl border overflow-hidden shadow-sm ${theme.border}`}>
          <div className={`bg-gradient-to-br ${theme.gradient} px-4 py-4`}>
            <div className="flex items-start gap-3">
              <Button
                variant="outline"
                size="sm"
                className="bg-white/90 hover:bg-white"
                onClick={handleBack}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>

              <div className="flex items-start gap-3 min-w-0">
                <div className={`w-12 h-12 rounded-xl ${theme.badgeBg} ring-2 ${theme.ring} flex items-center justify-center text-2xl flex-shrink-0 shadow-sm`}>
                  {getEmojiForType(formData.celebration_type)}
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-xl font-bold tracking-tight text-foreground">
                      {mode === 'edit' ? t('celebrations:edit') : t('celebrations:new')}
                    </h1>
                    <Badge variant={formData.celebration_type as any} size="sm" className="capitalize">
                      {getLabelForType(formData.celebration_type)}
                    </Badge>
                    {formData.celebration_subtype && (
                      <Badge variant="outline" size="sm" className="text-[10px]">
                        {getSubtypeLabel(formData.celebration_type, formData.celebration_subtype)}
                      </Badge>
                    )}
                  </div>

                  <p className="mt-1 text-xs text-muted-foreground">
                    {mode === 'edit'
                      ? t('celebrations:form.subtitle.edit')
                      : t('celebrations:form.subtitle.create')}
                  </p>

                  {mode === 'edit' && formData.title && (
                    <p className="mt-1 text-sm font-medium text-foreground/80 truncate">
                      {formData.title}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!embedded && mode === 'edit' && (
        <div className={`mb-4 rounded-xl border bg-white/90 shadow-sm ${theme.border}`}>
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
            <span className="text-xs font-semibold text-slate-700">{t('celebrations:form.jumpTo')}</span>
            {shortcutItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="inline-flex h-7 items-center rounded-md border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Main form card */}
          <div id={SECTION_IDS.basicDetails} className={`scroll-mt-24 bg-white rounded-xl border shadow-sm overflow-hidden ${theme.border}`}>
            <div className="flex items-start justify-between gap-3 border-b bg-gradient-to-r from-primary/5 to-transparent px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  {mode === 'edit' ? t('celebrations:form.sections.basicDetails') : t('celebrations:form.sections.createHeader')}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {mode === 'edit'
                    ? t('celebrations:form.sections.basicSubtitleEdit')
                    : t('celebrations:form.sections.basicSubtitleCreate')}
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={handleBack}>
                  {onBack ? t('common:actions.back') : t('common:actions.cancel')}
                </Button>
                <Button type="submit" size="sm" disabled={loading}>
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />
                      {mode === 'edit' ? t('common:status.saving') : t('celebrations:form.buttons.creating')}
                    </>
                  ) : (
                    <>
                      <Save className="h-3 w-3 mr-1" />
                      {mode === 'edit' ? t('common:actions.save') : t('common:actions.create')}
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="p-4 space-y-4">

            {mode === 'edit' && missingFieldLabels.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-amber-900">
                      {t('celebrations:form.missingCount', { count: missingFieldLabels.length })}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {missingFieldLabels.map((fieldLabel) => (
                        <span
                          key={fieldLabel}
                          className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900"
                        >
                          {fieldLabel}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <Label htmlFor="title" className="text-xs font-medium">{t('celebrations:form.labels.name')}</Label>
              <Input
                id="title"
                name="title"
                required
                value={formData.title}
                onChange={handleInputChange}
                placeholder={t('celebrations:form.placeholders.name')}
                className={isFieldMissing('title') ? 'mt-1 border-red-500 focus-visible:ring-red-500' : 'mt-1'}
              />
              {isFieldMissing('title') && (
                <p className="mt-1 text-[11px] text-red-600">{t('celebrations:form.validation.nameRequired')}</p>
              )}
            </div>

            {/* Type - inline */}
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label htmlFor="celebration_type" className="text-xs font-medium">{t('celebrations:fields.type')}</Label>
                {mode === 'edit' || wizardPresets ? (
                  <div className="mt-1 flex items-center gap-2 h-8 px-3 bg-muted rounded-md text-sm">
                    <span>{getEmojiForType(formData.celebration_type)}</span>
                    <span>{getLabelForType(formData.celebration_type)}</span>
                    {formData.celebration_subtype && (
                      <span className="text-xs text-muted-foreground">
                        · {getSubtypeLabel(formData.celebration_type, formData.celebration_subtype)}
                      </span>
                    )}
                  </div>
                ) : (
                  <select
                    id="celebration_type"
                    name="celebration_type"
                    title="Celebration type"
                    required
                    value={formData.celebration_type}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        celebration_type: e.target.value,
                        celebration_subtype: undefined, // reset subtype when type changes
                      }));
                    }}
                    className="mt-1 w-full h-8 px-2 text-sm rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                  >
                    {celebrationFormOptions.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.emoji} {type.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Subtype (conditional) — hidden when wizard already set it */}
            {!wizardPresets && subtypeOptions.length > 0 && (
              <div>
                <Label htmlFor="celebration_subtype" className="text-xs font-medium">{t('celebrations:form.labels.subtype')}</Label>
                <select
                  id="celebration_subtype"
                  name="celebration_subtype"
                  title="Celebration subtype"
                  value={formData.celebration_subtype || ''}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      celebration_subtype: e.target.value || undefined,
                    }));
                  }}
                  className="mt-1 w-full h-8 px-2 text-sm rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                >
                  <option value="">{t('celebrations:form.placeholders.subtypeNone')}</option>
                  {subtypeOptions.map(st => (
                    <option key={st.key} value={st.key}>{st.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Dates - inline */}
            <div>
              <Label className="text-xs font-medium flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {t('celebrations:form.labels.dates')}
              </Label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <div>
                  <span className="text-[10px] text-muted-foreground">{t('celebrations:form.labels.dateStart')}</span>
                  <Input
                    type="date"
                    id="start_date"
                    name="start_date"
                    required
                    value={formData.start_date}
                    onChange={handleInputChange}
                    className={isFieldMissing('start_date') ? 'border-red-500 focus-visible:ring-red-500' : undefined}
                    compact
                  />
                  {isFieldMissing('start_date') && (
                    <p className="mt-1 text-[11px] text-red-600">{t('celebrations:form.validation.startDateRequired')}</p>
                  )}
                </div>
                <div>
                  <span className="text-[10px] text-muted-foreground">{t('celebrations:form.labels.dateEnd')}</span>
                  <Input
                    type="date"
                    id="end_date"
                    name="end_date"
                    required
                    value={formData.end_date}
                    onChange={handleInputChange}
                    min={formData.start_date || undefined}
                    className={isFieldMissing('end_date') ? 'border-red-500 focus-visible:ring-red-500' : undefined}
                    compact
                  />
                  {isFieldMissing('end_date') && (
                    <p className="mt-1 text-[11px] text-red-600">{t('celebrations:form.validation.endDateRequired')}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Location and Radius */}
            <div className="grid grid-cols-[1fr,120px] gap-3">
               <div>
                  <Label htmlFor="location" className="text-xs font-medium">{t('celebrations:form.labels.locationBias')}</Label>
                  <LocationAutocomplete 
                    value={formData.location_name}
                    onLocationSelect={(loc) => {
                      setFormData(prev => ({
                        ...prev,
                        location_name: loc.name,
                        latitude: loc.lat,
                        longitude: loc.lng
                      }));
                    }}
                    placeholder={t('celebrations:form.placeholders.location')}
                    className="mt-1"
                    inputClassName={isFieldMissing('location_name') ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
                  />
                  {isFieldMissing('location_name') && (
                    <p className="mt-1 text-[11px] text-red-600">{t('celebrations:form.validation.locationRequired')}</p>
                  )}
               </div>
               <div>
                  <Label htmlFor="radius_miles" className="text-xs font-medium">{t('celebrations:form.labels.radiusMiles')}</Label>
                  <Input 
                    type="number"
                    id="radius_miles"
                    name="radius_miles"
                    value={formData.radius_miles || 10}
                    onChange={handleInputChange}
                    className="mt-1"
                    min={1}
                    max={500}
                  />
               </div>
            </div>

            {/* Description - compact */}
            <div>
              <Label htmlFor="description" className="text-xs font-medium">{t('celebrations:fields.description')}</Label>
              <textarea
                id="description"
                name="description"
                rows={2}
                value={formData.description}
                onChange={handleInputChange}
                className={isFieldMissing('description')
                  ? 'mt-1 w-full px-3 py-2 text-sm rounded-md border border-red-500 bg-background focus:ring-2 focus:ring-red-500 resize-none'
                  : 'mt-1 w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:ring-2 focus:ring-ring resize-none'}
                placeholder={t('celebrations:form.placeholders.description')}
              />
              {isFieldMissing('description') && (
                <p className="mt-1 text-[11px] text-red-600">{t('celebrations:form.validation.descriptionRequired')}</p>
              )}
            </div>

            </div>
          </div>

          {/* Celebrants Section */}
          <div id={SECTION_IDS.celebrants} className="scroll-mt-24">
            <CelebrationCelebrants 
              celebrationId={mode === 'edit' && id ? parseInt(id) : undefined}
              onUpdate={() => {}}
              initialCelebrants={mode === 'create' ? initialCelebrants : undefined}
              onCelebrantsChange={mode === 'create' ? setLocalCelebrants : undefined}
              containerClassName={theme.border}
            />
          </div>

          {/* Guests & Invitations Section (Create Mode) */}
          {mode === 'create' && (
            <div className="scroll-mt-24">
              <CelebrationGuestsCard
                mode={guestMode}
                onModeChange={setGuestMode}
                linkAccess={linkAccess}
                onLinkAccessChange={setLinkAccess}
                rsvpDeadline={rsvpDeadline}
                onRsvpDeadlineChange={setRsvpDeadline}
                allowPlusOnes={allowPlusOnes}
                onAllowPlusOnesChange={setAllowPlusOnes}
                plusOnesLimit={plusOnesLimit}
                onPlusOnesLimitChange={setPlusOnesLimit}
                guestEmails={guestEmails}
                onGuestEmailsChange={setGuestEmails}
                emailDispatchMode={emailDispatchMode}
                onEmailDispatchModeChange={setEmailDispatchMode}
                guestCountEstimate={guestCountEstimate}
                onGuestCountEstimateChange={setGuestCountEstimate}
                trackDietary={trackDietary}
                onTrackDietaryChange={setTrackDietary}
                seatingLayoutEnabled={seatingLayoutEnabled}
                onSeatingLayoutEnabledChange={setSeatingLayoutEnabled}
                groupTagsEnabled={groupTagsEnabled}
                onGroupTagsEnabledChange={setGroupTagsEnabled}
                eventSlugPreview={
                  (formData.title || 'my-celebration')
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, '') || 'celebration'
                }
              />
            </div>
          )}

          {/* Team Management Section - Only in Edit Mode */}
          {mode === 'edit' && id && (
            <div id={SECTION_IDS.teamMembers} className={`scroll-mt-24 bg-white rounded-xl border shadow-sm overflow-hidden ${theme.border}`}>
              <div className="flex items-center justify-between gap-3 border-b bg-gradient-to-r from-primary/5 to-transparent px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Users className="h-4 w-4" />
                    {t('celebrations:form.sections.teamMembers')}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('celebrations:form.teamSection.description')}
                  </p>
                </div>
                {!showAddTeamForm && (
                  <Button 
                    type="button"
                    size="sm" 
                    onClick={(e) => { e.preventDefault(); setShowAddTeamForm(true); }}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t('common:actions.add')}
                  </Button>
                )}
              </div>

              <div className="space-y-4 p-4">
                {teamError && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                    {teamError}
                  </div>
                )}

                {showAddTeamForm && (
                  <div className="mb-4">
                    <AddTeamMemberForm
                      celebrationId={parseInt(id)}
                      onSuccess={handleAddTeamSuccess}
                      onCancel={() => setShowAddTeamForm(false)}
                    />
                  </div>
                )}

                <TeamMembersList
                  celebrationId={parseInt(id)}
                  canEdit={true}
                  onEdit={setEditingMember}
                  onRemove={handleRemoveTeamMember}
                  refreshTrigger={teamRefreshTrigger}
                />
              </div>
            </div>
          )}

          {mode === 'create' && (
            <div id={SECTION_IDS.teamMembers} className={`scroll-mt-24 bg-white rounded-xl border shadow-sm overflow-hidden ${theme.border}`}>
              <div className="flex items-center justify-between gap-3 border-b bg-gradient-to-r from-primary/5 to-transparent px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Users className="h-4 w-4" />
                    {t('celebrations:form.sections.teamMembers')}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('celebrations:form.teamSection.saveFirst')}
                  </p>
                </div>
              </div>

              <div className="p-4">
                <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-600">
                  {t('celebrations:form.teamSection.availableAfterCreate')}
                </div>
              </div>
            </div>
          )}

          {/* Edit Team Member Modal */}
          {editingMember && id && (
            <EditTeamMemberModal
              member={editingMember}
              celebrationId={parseInt(id)}
              onSuccess={handleEditTeamSuccess}
              onCancel={() => setEditingMember(null)}
            />
          )}

        </form>
        {mode === 'edit' && id && <CelebrationQuotasSection celebrationId={parseInt(id, 10)} />}
      </div>
  );

  // When embedded in the wizard, return only the inner form content.
  if (embedded) {
    return formContent;
  }

  const breadcrumbItems = mode === 'edit' && id
    ? [
        { label: 'Home', to: '/home' },
        { label: 'Celebrations', to: '/celebrations' },
        { label: formData.title || 'Celebration', to: `/celebrations/${id}` },
        { label: 'Edit' },
      ]
    : [
        { label: 'Home', to: '/home' },
        { label: 'Celebrations', to: '/celebrations' },
        { label: 'New Celebration' },
      ];

  return (
    <PageContainer width="wide" className="space-y-4">
      <AppBreadcrumbs items={breadcrumbItems} />
      {formContent}
    </PageContainer>
  );
};
